import { Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { UserRole } from '@lysi-web/shared-types';
import { ProtectedRoute } from './components/ProtectedRoute';
import { useAuth } from './contexts/AuthContext';
import { DashboardLayout } from './components/layouts/DashboardLayout';
import { CustomerLayout } from './components/layouts/CustomerLayout';
import { PharmacyLayout } from './components/layouts/PharmacyLayout';
import { Login } from './pages/Login';
import { ForgotPassword } from './pages/ForgotPassword';
import { ResetPassword } from './pages/ResetPassword';
import { AdminDashboard } from './pages/AdminDashboard';
import { CustomerOps } from './pages/admin/CustomerOps';
import { DoctorDashboard } from './pages/DoctorDashboard';
import { DoctorProfile } from './pages/DoctorProfile';
import { TemplateList } from './pages/TemplateList';
import { CreateOrder } from './pages/CreateOrder';
import { OrderList } from './pages/OrderList';
import { OrderDetail } from './pages/OrderDetail';
import { MedicationsLookup } from './pages/MedicationsLookup';
import { MedicationPrint } from './pages/MedicationPrint';
import TrackingPage from './pages/TrackingPage';
import UnsubscribePage from './pages/UnsubscribePage';
import { UserList } from './pages/UserList';
import { CreateUser } from './pages/CreateUser';
import { EditUser } from './pages/EditUser';
import { SpecialtyList } from './pages/SpecialtyList';
import { InsuranceList } from './pages/InsuranceList';
import { PharmaceuticalLabsList } from './pages/PharmaceuticalLabsList';
import { PromotionList } from './pages/PromotionList';
import { PharmacyChainList } from './pages/PharmacyChainList';
import { MedicationCatalog } from './pages/MedicationCatalog';
import { PharmaceuticalForms } from './pages/PharmaceuticalForms';
import { Dosages } from './pages/Dosages';
import { Onboard } from './pages/Onboard';
import { Unauthorized } from './pages/Unauthorized';
import { Signup } from './pages/Signup';
import { VerifyEmail } from './pages/VerifyEmail';
import { ProfileComplete } from './pages/customer/ProfileComplete';
import { PublicCheckout } from './pages/customer/PublicCheckout';
import { Search as CustomerSearch } from './pages/customer/Search';
import { MyOrders } from './pages/customer/MyOrders';
import { MyOrderTracking } from './pages/customer/MyOrderTracking';
import { MyProfile } from './pages/customer/MyProfile';
import { PharmacyOrdersKanban } from './pages/pharmacy/PharmacyOrdersKanban';
import { PharmacyReports } from './pages/pharmacy/PharmacyReports';
import { ConsultantDashboard } from './pages/consultant/ConsultantDashboard';
import { CustomerRoute } from './components/auth/CustomerRoute';
import { PatientList } from './pages/PatientList';
import { PatientDetail } from './pages/PatientDetail';
import { ReportsComingSoon } from './pages/admin-reports/ReportsComingSoon';
import { InstallBanner } from './components/InstallBanner';

interface DoctorFeatureRouteProps {
  flag: 'canPrescribe' | 'canViewPatients';
  children: React.ReactNode;
}

const DoctorFeatureRoute: React.FC<DoctorFeatureRouteProps> = ({ flag, children }) => {
  const { user } = useAuth();
  if (!user?.[flag]) {
    return <Navigate to="/doctor/dashboard" replace />;
  }
  return <>{children}</>;
};

function App() {
  return (
    <>
      <InstallBanner />
      <Routes>
      {/* Public routes */}
      <Route path="/login" element={<Login />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/onboard" element={<Onboard />} />
      <Route path="/unauthorized" element={<Unauthorized />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/track/:trackingUUID" element={<TrackingPage />} />
      <Route path="/unsubscribe/:trackingUUID" element={<UnsubscribePage />} />
      <Route path="/profile-complete" element={<CustomerLayout><ProfileComplete /></CustomerLayout>} />
      <Route
        path="/checkout"
        element={
          <CustomerRoute>
            <CustomerLayout>
              <PublicCheckout />
            </CustomerLayout>
          </CustomerRoute>
        }
      />
      <Route
        path="/buscar"
        element={
          <CustomerRoute>
            <CustomerLayout>
              <CustomerSearch />
            </CustomerLayout>
          </CustomerRoute>
        }
      />
      <Route
        path="/mis-pedidos"
        element={
          <CustomerRoute>
            <CustomerLayout>
              <MyOrders />
            </CustomerLayout>
          </CustomerRoute>
        }
      />
      <Route
        path="/mis-pedidos/:trackingUUID"
        element={
          <CustomerRoute>
            <CustomerLayout>
              <MyOrderTracking />
            </CustomerLayout>
          </CustomerRoute>
        }
      />
      <Route
        path="/perfil"
        element={
          <CustomerRoute>
            <CustomerLayout>
              <MyProfile />
            </CustomerLayout>
          </CustomerRoute>
        }
      />

      {/* Admin routes - wrapped in DashboardLayout */}
      <Route
        path="/admin/*"
        element={
          <ProtectedRoute allowedRoles={[UserRole.SUPER_ADMIN]}>
            <DashboardLayout role="admin">
              <Outlet />
            </DashboardLayout>
          </ProtectedRoute>
        }
      >
        <Route path="dashboard" element={<AdminDashboard />} />
        <Route path="customer-ops" element={<CustomerOps />} />
        <Route path="users" element={<UserList />} />
        <Route path="users/create" element={<CreateUser />} />
        <Route path="users/:id" element={<EditUser />} />
        <Route path="specialties" element={<SpecialtyList />} />
        <Route path="insurance-companies" element={<InsuranceList />} />
        <Route path="pharmaceutical-labs" element={<PharmaceuticalLabsList />} />
        <Route path="promotions" element={<PromotionList />} />
        <Route path="pharmacy-chains" element={<PharmacyChainList />} />
        <Route path="catalog" element={<MedicationCatalog />} />
        <Route path="pharmaceutical-forms" element={<PharmaceuticalForms />} />
        <Route path="dosages" element={<Dosages />} />
        <Route path="reports" element={<ReportsComingSoon />} />
        <Route path="reports/*" element={<Navigate to="/admin/reports" replace />} />
        {/* Redirect /admin to /admin/dashboard */}
        <Route index element={<Navigate to="/admin/dashboard" replace />} />
      </Route>

      {/* Doctor routes - wrapped in DashboardLayout */}
      <Route
        path="/doctor/*"
        element={
          <ProtectedRoute allowedRoles={[UserRole.DOCTOR]}>
            <DashboardLayout role="doctor">
              <Outlet />
            </DashboardLayout>
          </ProtectedRoute>
        }
      >
        <Route path="dashboard" element={<DoctorDashboard />} />
        <Route path="profile" element={<DoctorProfile />} />
        <Route path="medications/print" element={<MedicationPrint />} />
        <Route path="medications" element={<MedicationsLookup />} />
        <Route
          path="templates"
          element={<DoctorFeatureRoute flag="canPrescribe"><TemplateList /></DoctorFeatureRoute>}
        />
        <Route
          path="orders"
          element={<DoctorFeatureRoute flag="canPrescribe"><OrderList /></DoctorFeatureRoute>}
        />
        <Route
          path="orders/new"
          element={<DoctorFeatureRoute flag="canPrescribe"><CreateOrder /></DoctorFeatureRoute>}
        />
        <Route
          path="orders/:id"
          element={<DoctorFeatureRoute flag="canPrescribe"><OrderDetail /></DoctorFeatureRoute>}
        />
        <Route
          path="patients"
          element={<DoctorFeatureRoute flag="canViewPatients"><PatientList /></DoctorFeatureRoute>}
        />
        <Route
          path="patients/:id"
          element={<DoctorFeatureRoute flag="canViewPatients"><PatientDetail /></DoctorFeatureRoute>}
        />
        {/* Redirect /doctor to /doctor/dashboard */}
        <Route index element={<Navigate to="/doctor/dashboard" replace />} />
      </Route>

      {/* Consultant routes - wrapped in DashboardLayout */}
      <Route
        path="/consultant/*"
        element={
          <ProtectedRoute allowedRoles={[UserRole.CONSULTANT]}>
            <DashboardLayout role="consultant">
              <Outlet />
            </DashboardLayout>
          </ProtectedRoute>
        }
      >
        <Route path="dashboard" element={<ConsultantDashboard />} />
        <Route path="medications/print" element={<MedicationPrint />} />
        <Route path="medications" element={<MedicationsLookup />} />
        <Route index element={<Navigate to="/consultant/dashboard" replace />} />
      </Route>

      {/* Pharmacy routes - wrapped in PharmacyLayout */}
      <Route
        path="/pharmacy"
        element={
          <ProtectedRoute allowedRoles={[UserRole.PHARMACY_MANAGER, UserRole.PHARMACY_EMPLOYEE]}>
            <PharmacyLayout />
          </ProtectedRoute>
        }
      >
        <Route path="orders" element={<PharmacyOrdersKanban />} />
        <Route
          path="reports"
          element={
            <ProtectedRoute allowedRoles={[UserRole.PHARMACY_MANAGER]}>
              <PharmacyReports />
            </ProtectedRoute>
          }
        />
        <Route index element={<Navigate to="orders" replace />} />
      </Route>

      {/* Legacy redirects for doctor routes */}
      <Route path="/orders/:id" element={<Navigate to="/doctor/orders/:id" replace />} />
      <Route path="/patients" element={<Navigate to="/doctor/patients" replace />} />
      <Route path="/patients/:id" element={<Navigate to="/doctor/patients/:id" replace />} />

      {/* Default route */}
      <Route path="/" element={<Navigate to="/login" replace />} />

      {/* Catch all - redirect to login */}
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
    </>
  );
}

export default App;
